import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { Alert, Muted } from '@/components/ui'
import { fetchColaboradores } from '@/features/rh/api'
import type { ColaboradorResumo } from '@/features/rh/types'
import {
  createRescisao,
  fetchBeneficiosVinculos,
  simularRescisao,
  type RescisaoCalculo,
} from './api'
import {
  RESCISAO_DOCS,
  RESCISAO_ETAPAS,
  RESCISAO_PENDENCIAS,
  RESCISAO_TIPOS,
  diasAvisoPrevio,
  temAvisoPrevio,
} from './rescisaoConstants'
import { formatDate, formatMoney } from './utils'
import './rescisao.css'

type Props = {
  onCancel: () => void
  onSuccess: (rescisaoId: string) => void
  initialColaboradorId?: string
}

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function money(n?: number | null) {
  return formatMoney(n ?? null)
}

export function RescisaoWizard({ onCancel, onSuccess, initialColaboradorId }: Props) {
  const [etapa, setEtapa] = useState(1)
  const [busca, setBusca] = useState('')
  const [q, setQ] = useState('')
  const [colab, setColab] = useState<ColaboradorResumo | null>(null)
  const [tipo, setTipo] = useState('sem_justa_causa')
  const [dataDemissao, setDataDemissao] = useState(todayIso())
  const [avisoIndenizado, setAvisoIndenizado] = useState(false)
  const [feriasVencidas, setFeriasVencidas] = useState(30)
  const [abono, setAbono] = useState(0)
  const [pendencias, setPendencias] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(RESCISAO_PENDENCIAS.map((p) => [p.id, p.id === 'epi'])),
  )
  const [benefCancelar, setBenefCancelar] = useState<Record<string, boolean>>({})
  const [docsGerados, setDocsGerados] = useState<Record<string, boolean>>({})
  const [aceite, setAceite] = useState(false)
  const [error, setError] = useState('')
  const [calc, setCalc] = useState<RescisaoCalculo | null>(null)
  const [calcError, setCalcError] = useState('')

  const colaboradores = useQuery({
    queryKey: ['dp', 'rescisao', 'colabs', q],
    queryFn: () =>
      fetchColaboradores({
        busca: q || undefined,
        status: 'ativo',
        por_pagina: 40,
        ordenar: 'nome',
      }),
  })

  const vinculos = useQuery({
    queryKey: ['dp', 'rescisao', 'beneficios', colab?.id],
    queryFn: fetchBeneficiosVinculos,
    enabled: Boolean(colab?.id) && etapa >= 5,
  })

  const benefsColab = useMemo(() => {
    const all = vinculos.data?.dados || []
    if (!colab?.id) return []
    return all.filter((v) => String(v.colaborador) === String(colab.id))
  }, [vinculos.data, colab?.id])

  useEffect(() => {
    if (!initialColaboradorId || colab) return
    const found = (colaboradores.data?.dados || []).find((c) => c.id === initialColaboradorId)
    if (found) setColab(found)
  }, [initialColaboradorId, colaboradores.data, colab])

  const diasAviso = diasAvisoPrevio(colab?.data_admissao, dataDemissao)
  const mostraAviso = temAvisoPrevio(tipo)

  const simMut = useMutation({
    mutationFn: () => {
      if (!colab) throw new Error('Selecione um colaborador.')
      return simularRescisao({
        colaborador_id: colab.id,
        tipo,
        data_demissao: dataDemissao,
        aviso_indenizado: avisoIndenizado,
        dias_ferias_vencidas: feriasVencidas,
      })
    },
    onSuccess: (res) => {
      setCalcError('')
      setCalc(res.resultado || res)
    },
    onError: (err) => {
      setCalc(null)
      setCalcError(err instanceof ApiError ? err.message : 'Falha ao calcular rescisão.')
    },
  })

  useEffect(() => {
    if (!colab || !dataDemissao || !tipo) return
    const t = window.setTimeout(() => simMut.mutate(), 350)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colab?.id, tipo, dataDemissao, avisoIndenizado, feriasVencidas])

  const saveMut = useMutation({
    mutationFn: () => {
      if (!colab) throw new Error('Selecione um colaborador.')
      return createRescisao({
        colaborador_id: colab.id,
        tipo,
        data_demissao: dataDemissao,
        aviso_indenizado: avisoIndenizado,
        dias_ferias_vencidas: feriasVencidas,
        finalizar: true,
        desligar_colaborador: true,
        etapa_atual: 7,
        dados_wizard: {
          pendencias,
          beneficios_encerrar: Object.entries(benefCancelar)
            .filter(([, v]) => v)
            .map(([id]) => id),
          abono,
          documentos: docsGerados,
          aceite_conferencia: true,
        },
      })
    },
    onSuccess: (res) => {
      if (res.dados?.id) onSuccess(res.dados.id)
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Falha ao finalizar rescisão.')
    },
  })

  const liquido = calc?.totalLiquido
  const memoria = calc?.memoria_calculo || []

  function validar(): string | null {
    if (!colab) return 'Selecione o colaborador.'
    if (!dataDemissao) return 'Informe a data de demissão.'
    if (etapa === 6 && !aceite) return 'Confirme a revisão do cálculo antes de continuar.'
    if (etapa >= 6 && !calc) return calcError || 'Aguarde o cálculo da rescisão.'
    return null
  }

  function avancar() {
    setError('')
    const err = validar()
    if (err) {
      setError(err)
      return
    }
    if (etapa < RESCISAO_ETAPAS.length) setEtapa((e) => e + 1)
  }

  function voltar() {
    setError('')
    if (etapa > 1) setEtapa((e) => e - 1)
  }

  function finalizar() {
    setError('')
    const err = validar()
    if (err) {
      setError(err)
      return
    }
    if (!aceite) {
      setError('Confirme a revisão do cálculo antes de finalizar.')
      return
    }
    saveMut.mutate()
  }

  function toggleDoc(id: string) {
    setDocsGerados((d) => ({ ...d, [id]: true }))
  }

  function gerarTodos() {
    setDocsGerados(Object.fromEntries(RESCISAO_DOCS.map((d) => [d.id, true])))
  }

  const lista = colaboradores.data?.dados || []

  return (
    <section className="hi-stack">
      {!colab ? (
        <div className="hi-rescisao-colab-pick">
          <Muted>Selecione o colaborador para iniciar o desligamento.</Muted>
          <form
            className="hi-filters"
            onSubmit={(e) => {
              e.preventDefault()
              setQ(busca.trim())
            }}
          >
            <input
              type="search"
              placeholder="Buscar colaborador ativo"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              aria-label="Buscar colaborador"
            />
            <button type="submit" className="btn btn-secondary">
              Buscar
            </button>
          </form>
          {colaboradores.isLoading ? <Muted>Carregando…</Muted> : null}
          {colaboradores.isError ? (
            <Alert tone="danger">
              {colaboradores.error instanceof ApiError
                ? colaboradores.error.message
                : 'Falha ao buscar colaboradores.'}
            </Alert>
          ) : null}
          <div className="hi-rescisao-colab-list">
            {lista.map((c) => (
              <button
                key={c.id}
                type="button"
                className="hi-rescisao-colab-item"
                onClick={() => setColab(c)}
              >
                <strong>{c.nome}</strong>
                <Muted>
                  {[c.cargo_nome, c.departamento_nome].filter(Boolean).join(' · ') || '—'}
                  {c.data_admissao ? ` · Admissão ${formatDate(c.data_admissao)}` : ''}
                </Muted>
              </button>
            ))}
            {!colaboradores.isLoading && lista.length === 0 ? (
              <EmptyLocal>Nenhum colaborador encontrado.</EmptyLocal>
            ) : null}
          </div>
          <div className="hi-rescisao-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="hi-rescisao-stepper" role="tablist" aria-label="Etapas da rescisão">
            {RESCISAO_ETAPAS.map((e) => (
              <button
                key={e.id}
                type="button"
                className={`hi-rescisao-step${etapa === e.id ? ' is-active' : ''}${
                  e.id < etapa ? ' is-done' : ''
                }`}
                onClick={() => {
                  if (e.id <= etapa) setEtapa(e.id)
                }}
              >
                <span className="hi-rescisao-step-n">{e.id < etapa ? '✓' : e.id}</span>
                {e.titulo}
              </button>
            ))}
          </div>

          <Muted>
            {colab.nome}
            {colab.cargo_nome ? ` · ${colab.cargo_nome}` : ''}
            {' · '}
            Etapa {etapa} de {RESCISAO_ETAPAS.length}
            {liquido != null ? ` · Líquido ${money(liquido)}` : ''}
          </Muted>

          {error ? <Alert tone="danger">{error}</Alert> : null}
          {calcError && etapa >= 6 ? <Alert tone="danger">{calcError}</Alert> : null}

          {etapa === 1 ? (
            <div className="hi-stack">
              <h3>{RESCISAO_ETAPAS[0].titulo}</h3>
              <Muted>{RESCISAO_ETAPAS[0].desc}</Muted>
              <div className="hi-rescisao-tipo-grid">
                {RESCISAO_TIPOS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`hi-rescisao-tipo${tipo === t.id ? ' is-active' : ''}`}
                    onClick={() => setTipo(t.id)}
                  >
                    <strong>{t.label}</strong>
                    <small>{t.desc}</small>
                    <Muted>{t.lei}</Muted>
                  </button>
                ))}
              </div>
              <label className="hi-field">
                <span>Data do desligamento</span>
                <input
                  type="date"
                  value={dataDemissao}
                  onChange={(e) => setDataDemissao(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setColab(null)}
              >
                Trocar colaborador
              </button>
            </div>
          ) : null}

          {etapa === 2 ? (
            <div className="hi-stack">
              <h3>{RESCISAO_ETAPAS[1].titulo}</h3>
              <div className="hi-rescisao-info">
                <div>
                  <Muted>Admissão</Muted>
                  <strong>{formatDate(colab.data_admissao)}</strong>
                </div>
                <div>
                  <Muted>Dias de aviso</Muted>
                  <strong>{diasAviso} dias</strong>
                  <Muted>Lei 12.506/2011 · máx. 90</Muted>
                </div>
              </div>
              {!mostraAviso ? (
                <Alert tone="info">
                  Para este tipo, as condições de aviso prévio são diferenciadas.
                </Alert>
              ) : (
                <div className="hi-stack">
                  <button
                    type="button"
                    className={`hi-rescisao-opcao${!avisoIndenizado ? ' is-active' : ''}`}
                    onClick={() => setAvisoIndenizado(false)}
                  >
                    <strong>Aviso prévio trabalhado</strong>
                    <Muted>Colaborador trabalha os {diasAviso} dias. Sem custo adicional.</Muted>
                  </button>
                  <button
                    type="button"
                    className={`hi-rescisao-opcao${avisoIndenizado ? ' is-active' : ''}`}
                    onClick={() => setAvisoIndenizado(true)}
                  >
                    <strong>Aviso prévio indenizado</strong>
                    <Muted>Empresa paga os {diasAviso} dias sem exigir trabalho.</Muted>
                  </button>
                </div>
              )}
              {tipo === 'justa_causa' ? (
                <Alert tone="danger">
                  Justa causa requer documentação de advertências e prova da falta grave (Art. 482
                  CLT).
                </Alert>
              ) : null}
            </div>
          ) : null}

          {etapa === 3 ? (
            <div className="hi-stack">
              <h3>{RESCISAO_ETAPAS[2].titulo}</h3>
              <Muted>Marque os itens já resolvidos antes de prosseguir.</Muted>
              <div className="hi-rescisao-checklist">
                {RESCISAO_PENDENCIAS.map((p) => (
                  <label
                    key={p.id}
                    className={`hi-rescisao-check${pendencias[p.id] ? ' is-ok' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(pendencias[p.id])}
                      onChange={(e) =>
                        setPendencias((cur) => ({ ...cur, [p.id]: e.target.checked }))
                      }
                    />
                    <span>{p.label}</span>
                  </label>
                ))}
              </div>
              <Alert tone="info">
                Exame demissional: prazo de {tipo === 'sem_justa_causa' ? '5' : '10'} dias úteis após
                o desligamento (NR-7).
              </Alert>
            </div>
          ) : null}

          {etapa === 4 ? (
            <div className="hi-stack">
              <h3>{RESCISAO_ETAPAS[3].titulo}</h3>
              <Muted>Configure o saldo de férias a indenizar na rescisão.</Muted>
              <div className="hi-fields">
                <label className="hi-field">
                  <span>Dias de férias vencidas</span>
                  <input
                    type="number"
                    min={0}
                    max={60}
                    value={feriasVencidas}
                    onChange={(e) => setFeriasVencidas(Number(e.target.value) || 0)}
                  />
                </label>
                <label className="hi-field">
                  <span>Dias de abono (opcional)</span>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    value={abono}
                    onChange={(e) => setAbono(Number(e.target.value) || 0)}
                  />
                </label>
              </div>
              {feriasVencidas > 0 ? (
                <Alert tone="info">
                  Férias vencidas: se não concedidas no prazo, a empresa pode dever em dobro (Art.
                  137 CLT).
                </Alert>
              ) : null}
            </div>
          ) : null}

          {etapa === 5 ? (
            <div className="hi-stack">
              <h3>{RESCISAO_ETAPAS[4].titulo}</h3>
              <Muted>Confirme os benefícios a encerrar na data do último dia trabalhado.</Muted>
              {vinculos.isLoading ? <Muted>Carregando benefícios…</Muted> : null}
              {benefsColab.length === 0 && !vinculos.isLoading ? (
                <Alert tone="info">Nenhum vínculo de benefício ativo encontrado para este colaborador.</Alert>
              ) : (
                <div className="hi-rescisao-checklist">
                  {benefsColab.map((b) => (
                    <label
                      key={b.id}
                      className={`hi-rescisao-check${benefCancelar[b.id] ? ' is-ok' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(benefCancelar[b.id])}
                        onChange={(e) =>
                          setBenefCancelar((cur) => ({ ...cur, [b.id]: e.target.checked }))
                        }
                      />
                      <span>
                        {b.beneficio_nome || 'Benefício'}
                        {b.status_display ? ` · ${b.status_display}` : ''}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {etapa === 6 ? (
            <div className="hi-stack">
              <h3>{RESCISAO_ETAPAS[5].titulo}</h3>
              {simMut.isPending && !calc ? <Muted>Calculando…</Muted> : null}
              {calc ? (
                <>
                  <div className="hi-rescisao-hero">
                    <span>Rescisão líquida</span>
                    <strong>{money(calc.totalLiquido)}</strong>
                    <span>
                      {RESCISAO_TIPOS.find((t) => t.id === tipo)?.label || tipo} ·{' '}
                      {formatDate(dataDemissao)}
                    </span>
                  </div>
                  <div className="hi-rescisao-memoria">
                    {memoria.map((l, i) => (
                      <div
                        key={`${l.item}-${i}`}
                        className={`hi-rescisao-mem-linha${
                          l.tipo === 'desconto' ? ' is-desconto' : ''
                        }${l.tipo === 'total' ? ' is-total' : ''}`}
                      >
                        <span>
                          {l.sinal ? `${l.sinal} ` : ''}
                          {l.item}
                        </span>
                        <span>{money(l.valor)}</span>
                      </div>
                    ))}
                    {calc.saldoFGTS ? (
                      <Muted>FGTS estimado: {money(calc.saldoFGTS)} (empresa → CEF)</Muted>
                    ) : null}
                  </div>
                  <label className="hi-rescisao-check">
                    <input
                      type="checkbox"
                      checked={aceite}
                      onChange={(e) => setAceite(e.target.checked)}
                    />
                    <span>
                      Confirmo que revisei os valores e autorizo a geração da rescisão para{' '}
                      <strong>{colab.nome}</strong>.
                    </span>
                  </label>
                </>
              ) : null}
            </div>
          ) : null}

          {etapa === 7 ? (
            <div className="hi-stack">
              <h3>{RESCISAO_ETAPAS[6].titulo}</h3>
              <Muted>
                Marque os documentos gerados. Após finalizar, o processo fica disponível na lista e
                o colaborador é marcado como desligado.
              </Muted>
              <div className="hi-rescisao-docs">
                {RESCISAO_DOCS.map((d) => (
                  <div
                    key={d.id}
                    className={`hi-rescisao-doc${docsGerados[d.id] ? ' is-done' : ''}`}
                  >
                    <strong>{d.nome}</strong>
                    <Muted>{d.desc}</Muted>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => toggleDoc(d.id)}
                    >
                      {docsGerados[d.id] ? 'Gerado' : 'Marcar gerado'}
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="btn btn-secondary" onClick={gerarTodos}>
                Marcar todos os documentos
              </button>
              <Alert tone="info">
                PDF/TRCT automatizado e assinatura digital entram na próxima onda — por ora o
                registro oficial da rescisão é persistido na API.
              </Alert>
            </div>
          ) : null}

          <div className="hi-rescisao-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {etapa > 1 ? (
                <button type="button" className="btn btn-secondary" onClick={voltar}>
                  Anterior
                </button>
              ) : null}
              {etapa < RESCISAO_ETAPAS.length ? (
                <button type="button" className="btn btn-primary" onClick={avancar}>
                  Próximo
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saveMut.isPending}
                  onClick={finalizar}
                >
                  {saveMut.isPending ? 'Finalizando…' : 'Finalizar rescisão'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  )
}

function EmptyLocal({ children }: { children: ReactNode }) {
  return <Muted>{children}</Muted>
}

export default RescisaoWizard
